import { defineStore } from 'pinia';
import { LanguageTranslationStatus, TranslationLanguage } from '@/entities/checklist';
import { getLanguages } from '@/services/api/resources/checklist';

interface State {
  selectedLanguageId: number;
  translationLanguages: Array<TranslationLanguage>;
  pollingIntervalId: NodeJS.Timeout | null;
}

const POLLING_INTERVAL_MS = 30000; // 30 seconds

export const useTranslationLanguagesStore = defineStore('translationLanguages', {
  state: (): State => ({
    selectedLanguageId: 0,
    translationLanguages: [],
    pollingIntervalId: null,
  }),
  getters: {
    hasIncompleteLanguages(): boolean {
      return this.translationLanguages.some(lang => lang.status !== LanguageTranslationStatus.completed);
    },
    hasLanguages(): boolean {
      return this.translationLanguages.length > 0;
    },
    selectedLanguage(): TranslationLanguage | undefined {
      return this.translationLanguages.find(lang => lang.id === this.selectedLanguageId);
    },
    isOriginalLanguageSelected(): boolean {
      return this.selectedLanguageId === 0;
    },
    isAiEnabledLanguageSelected(): boolean {
      return this.selectedLanguageId === 0 ? false : (this.selectedLanguage?.aiEnabled ?? false);
    },
    isManualLanguageSelected(): boolean {
      return this.selectedLanguageId === 0 ? false : (this.selectedLanguage?.aiEnabled === false);
    },
  },
  actions: {
    initialize(languages: Array<TranslationLanguage>) {
      this.translationLanguages = [...languages];
    },
    getLanguage(locale: string) {
      return this.translationLanguages.find(lang => lang.locale === locale);
    },
    setSelectedLanguageId(id: number) {
      this.selectedLanguageId = id;
    },
    updateLanguages(languages: Array<TranslationLanguage>) {
      this.translationLanguages = languages;
    },
    async startPolling(checklistId: number) {
      if (this.pollingIntervalId) {
        return;
      }

      this.pollingIntervalId = setInterval(async () => {
        const result = await getLanguages(checklistId)();

        if (result._tag === 'Right') {
          this.updateLanguages(result.right.data);

          if (!this.hasIncompleteLanguages) {
            this.stopPolling();
          }
        }
      }, POLLING_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
      }
    },
  },
});
